import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useFriends } from '../context/FriendContext';
import { getLevelInfo } from '../utils/xpUtils';
import ChatModal from '../components/ChatModal';
import ChallengeModal from '../components/ChallengeModal';
import Icon from '../components/Icon';

const ScoreLabel = ({ score, opponentScore, isWinner }) => {
  if (score == null) return <span className="text-zinc-500 text-xs">En attente</span>;
  return (
    <span className={`text-sm font-black ${isWinner === true ? 'text-green-400' : isWinner === false ? 'text-red-400' : 'text-zinc-400'}`}>
      {score} pts
    </span>
  );
};

const ChallengeCard = ({ challenge, myId, onAccept, onDecline }) => {
  const isChallenger = String(challenge.challenger?._id) === String(myId);
  const opponent = isChallenger ? challenge.challenged : challenge.challenger;
  const myScore = isChallenger ? challenge.challengerScore : challenge.challengedScore;
  const opScore = isChallenger ? challenge.challengedScore : challenge.challengerScore;
  const isWinner = challenge.winner
    ? String(challenge.winner._id ?? challenge.winner) === String(myId)
    : null;
  const isDraw = challenge.status === 'completed' && !challenge.winner && myScore != null;

  const statusBadge = {
    pending:   isChallenger
      ? <span className="text-xs bg-zinc-700/60 text-zinc-400 px-2 py-0.5 rounded-full">En attente de réponse</span>
      : <span className="text-xs bg-yellow-500/20 text-yellow-400 border border-yellow-500/30 px-2 py-0.5 rounded-full animate-pulse">Défi reçu !</span>,
    accepted:  <span className="text-xs bg-blue-500/20 text-blue-400 border border-blue-500/30 px-2 py-0.5 rounded-full">En cours</span>,
    declined:  <span className="text-xs bg-zinc-700 text-zinc-500 px-2 py-0.5 rounded-full">Refusé</span>,
    completed: <span className={`text-sm font-black ${isWinner ? 'text-green-400' : isDraw ? 'text-zinc-400' : 'text-red-400'}`}>
      {isWinner ? '+1 000 XP' : isDraw ? 'Égalité' : 'Défaite'}
    </span>,
  }[challenge.status];

  return (
    <div className={`card flex flex-col gap-3 ${
      challenge.status === 'pending' && !isChallenger ? 'border-yellow-500/30 bg-yellow-500/5' : ''
    } ${challenge.status === 'completed' && isWinner ? 'border-green-500/20 bg-green-500/5' : ''}`}>

      {/* Header */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <p className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1">
            GP · R{challenge.race?.round} · {challenge.race?.raceName}
          </p>
          <p className="text-white text-sm font-bold truncate">
            {isChallenger ? 'Tu as défié ' : 'Défi de '}
            <span className="text-f1red">{opponent?.username}</span>
          </p>
        </div>
        <div className="shrink-0 text-right">{statusBadge}</div>
      </div>

      {/* Mechanic reminder (non-completed) */}
      {challenge.status !== 'completed' && challenge.status !== 'declined' && (
        <p className="text-[11px] text-zinc-500 bg-zinc-800/50 rounded-lg px-3 py-1.5">
          Qui marque le plus de points pronostic sur ce GP ? · Gagnant = <span className="text-yellow-400 font-bold">+1 000 XP</span>
        </p>
      )}

      {/* Score breakdown (completed) */}
      {challenge.status === 'completed' && (
        <div className="grid grid-cols-3 items-center gap-2 bg-zinc-800/50 rounded-xl px-3 py-2.5">
          <div className="text-center">
            <p className="text-[10px] text-zinc-500 mb-0.5">Toi</p>
            <ScoreLabel score={myScore} isWinner={isWinner === true} />
          </div>
          <div className="text-center">
            <span className="text-zinc-600 text-xs font-black">VS</span>
          </div>
          <div className="text-center">
            <p className="text-[10px] text-zinc-500 mb-0.5">{opponent?.username}</p>
            <ScoreLabel score={opScore} isWinner={isWinner === false && !isDraw} />
          </div>
        </div>
      )}

      {/* Accept / decline */}
      {challenge.status === 'pending' && !isChallenger && (
        <div className="flex gap-2">
          <button onClick={() => onAccept(challenge._id)} className="flex-1 btn-primary py-2 text-xs">Accepter le défi</button>
          <button onClick={() => onDecline(challenge._id)} className="flex-1 btn-secondary py-2 text-xs">Refuser</button>
        </div>
      )}
    </div>
  );
};

const GAME_ICONS = { lightspeed: 'LS', cirquiz: 'CQ', podiumquiz: 'PQ', pronostic: 'PRO' };

const Friends = () => {
  const { user } = useAuth();
  const { friends, fetchFriends, removeFriend } = useFriends() || {};
  const [challenges, setChallenges] = useState([]);
  const [rivalries, setRivalries]   = useState([]);
  const [unread, setUnread] = useState({});
  const [chatFriend, setChatFriend] = useState(null);
  const [challengeFriend, setChallengeFriend] = useState(null);
  const [tab, setTab] = useState('friends');

  const fetchChallenges = useCallback(() => {
    api.get('/challenges').then(r => setChallenges(r.data)).catch(() => {});
  }, []);

  const fetchRivalries = useCallback(() => {
    api.get('/challenges/rivalries').then(r => setRivalries(r.data)).catch(() => {});
  }, []);

  const fetchUnread = useCallback(() => {
    api.get('/messages/unread-count').then(r => setUnread(r.data.bySender || {})).catch(() => {});
  }, []);

  useEffect(() => {
    fetchFriends?.();
    fetchChallenges();
    fetchUnread();
    fetchRivalries();
    const interval = setInterval(() => { fetchUnread(); fetchChallenges(); }, 15000);
    return () => clearInterval(interval);
  }, []);

  const handleAcceptChallenge = async (id) => {
    await api.patch(`/challenges/${id}/accept`);
    fetchChallenges();
  };

  const handleDeclineChallenge = async (id) => {
    await api.patch(`/challenges/${id}/decline`);
    fetchChallenges();
  };

  const pendingChallenges = challenges.filter(c => c.status === 'pending' && String(c.challenged?._id) === String(user?.id));

  return (
    <div className="max-w-3xl mx-auto px-4 py-10">
      <div className="mb-8">
        <p className="text-zinc-500 text-xs uppercase tracking-widest font-medium mb-1">Social</p>
        <h1 className="text-3xl font-black text-white">Mes Amis</h1>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-zinc-900 border border-zinc-800 rounded-xl p-1 mb-6 flex-wrap">
        {[
          { key: 'friends',    label: `Amis (${friends?.length ?? 0})` },
          { key: 'challenges', label: `Défis${pendingChallenges.length > 0 ? ` 🔴` : ` (${challenges.length})`}` },
          { key: 'rivalries',  label: `Rivalités${rivalries.length > 0 ? ` (${rivalries.length})` : ''}` },
        ].map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`px-4 py-1.5 rounded-lg text-sm font-semibold transition-all ${
              tab === t.key ? 'bg-zinc-700 text-white' : 'text-zinc-500 hover:text-zinc-300'
            }`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* FRIENDS TAB */}
      {tab === 'friends' && (
        <div className="flex flex-col gap-4">
          {!friends?.length ? (
            <div className="card text-center py-12">
              <p className="text-zinc-500 text-sm mb-2">Aucun ami pour l'instant.</p>
              <Link to="/joueurs" className="text-f1red text-sm hover:underline">Trouve des joueurs →</Link>
            </div>
          ) : friends.map(friend => {
            const unreadCount = unread[friend._id] || 0;
            const { level } = getLevelInfo(friend.xp ?? 0);
            return (
              <div key={friend._id} className="card flex items-center gap-4">
                {/* Avatar */}
                <Link to={`/joueurs/${friend._id}`} className="shrink-0">
                  <div className="w-12 h-12 rounded-full overflow-hidden border-2 border-zinc-700 hover:border-zinc-500 transition-colors">
                    {friend.avatar
                      ? <img src={friend.avatar} alt="" className="w-full h-full object-cover" />
                      : <div className="w-full h-full bg-f1red/20 flex items-center justify-center text-lg font-black text-f1red">
                          {friend.username?.[0]?.toUpperCase()}
                        </div>
                    }
                  </div>
                </Link>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <Link to={`/joueurs/${friend._id}`} className="text-white font-bold text-base hover:text-f1red transition-colors truncate block">
                    {friend.username}
                  </Link>
                  <p className="text-zinc-500 text-xs">Niveau {level} · {(friend.xp ?? 0).toLocaleString()} XP</p>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2 shrink-0">
                  <button onClick={() => setChatFriend(friend)}
                    className="relative btn-secondary text-xs py-1.5 px-3 flex items-center gap-1.5">
                    <Icon name="message" size={12} />
                    {unreadCount > 0 && (
                      <span className="absolute -top-1.5 -right-1.5 bg-f1red text-white text-[9px] font-black w-4 h-4 rounded-full flex items-center justify-center">
                        {unreadCount > 9 ? '9+' : unreadCount}
                      </span>
                    )}
                  </button>
                  <button onClick={() => setChallengeFriend(friend)}
                    className="btn-secondary text-xs py-1.5 px-3"><Icon name="sword" size={12} /></button>
                  <button onClick={() => removeFriend?.(friend._id)}
                    className="text-zinc-600 hover:text-red-400 text-xs py-1.5 px-2 rounded-lg transition-colors border border-transparent hover:border-red-900/40">
                    ✕
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* CHALLENGES TAB */}
      {tab === 'challenges' && (
        <div className="flex flex-col gap-4">
          {challenges.length === 0 ? (
            <div className="card text-center py-12">
              <p className="text-zinc-500 text-sm mb-2">Aucun défi en cours.</p>
              <p className="text-zinc-600 text-xs">Défie un ami depuis sa page profil ou ici.</p>
            </div>
          ) : challenges.map(c => (
            <ChallengeCard key={c._id} challenge={c} myId={user?.id}
              onAccept={handleAcceptChallenge} onDecline={handleDeclineChallenge} />
          ))}
        </div>
      )}

      {/* RIVALRIES TAB */}
      {tab === 'rivalries' && (
        <div className="flex flex-col gap-3">
          {rivalries.length === 0 ? (
            <div className="card text-center py-12">
              <Icon name="sword" size={32} className="text-zinc-700 mx-auto mb-3" />
              <p className="text-zinc-500 text-sm mb-1">Aucune rivalité pour l'instant.</p>
              <p className="text-zinc-600 text-xs">Défie des amis aux mini-jeux pour construire ton palmarès.</p>
            </div>
          ) : rivalries.map(r => {
            const total = r.wins + r.losses + r.draws;
            const winPct = total > 0 ? Math.round((r.wins / total) * 100) : 0;
            const isWinning = r.wins > r.losses;
            const isTied    = r.wins === r.losses;
            return (
              <div key={r.opponent._id} className="card flex items-center gap-4">
                <Link to={`/joueurs/${r.opponent._id}`} className="shrink-0">
                  <div className="w-12 h-12 rounded-full overflow-hidden border-2 border-zinc-700 hover:border-zinc-500 transition-colors">
                    {r.opponent.avatar
                      ? <img src={r.opponent.avatar} alt="" className="w-full h-full object-cover" />
                      : <div className="w-full h-full bg-f1red/20 flex items-center justify-center text-lg font-black text-f1red">
                          {r.opponent.username?.[0]?.toUpperCase()}
                        </div>
                    }
                  </div>
                </Link>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <Link to={`/joueurs/${r.opponent._id}`} className="text-white font-bold text-sm hover:text-f1red transition-colors">
                      {r.opponent.username}
                    </Link>
                    <span className={`text-[10px] font-black px-1.5 py-0.5 rounded-full ${
                      isWinning ? 'bg-green-500/15 text-green-400' :
                      isTied    ? 'bg-zinc-700 text-zinc-400' :
                                  'bg-red-500/15 text-red-400'
                    }`}>
                      {isWinning ? '↑ Avantage' : isTied ? '= Égalité' : '↓ Défavorable'}
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-green-400 text-xs font-black">{r.wins}V</span>
                    <span className="text-red-400   text-xs font-black">{r.losses}D</span>
                    {r.draws > 0 && <span className="text-zinc-500 text-xs font-black">{r.draws}N</span>}
                    <span className="text-zinc-600 text-[10px]">· {total} défi{total > 1 ? 's' : ''}</span>
                  </div>
                  {/* Win-rate bar */}
                  <div className="mt-1.5 h-1.5 rounded-full bg-zinc-800 overflow-hidden w-full">
                    <div className="h-full rounded-full transition-all"
                      style={{ width:`${winPct}%`, background: winPct >= 50 ? '#22c55e' : '#e10600' }} />
                  </div>
                  {/* Games played together */}
                  {r.games?.length > 0 && (
                    <div className="flex gap-1 mt-1.5">
                      {r.games.map(g => (
                        <span key={g} className="text-[9px] px-1.5 py-0.5 rounded bg-zinc-800 text-zinc-500 font-semibold">
                          {GAME_ICONS[g] ?? '🎮'} {g}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modals */}
      {chatFriend && (
        <ChatModal friend={chatFriend} onClose={() => { setChatFriend(null); fetchUnread(); }} />
      )}
      {challengeFriend && (
        <ChallengeModal friend={challengeFriend} onClose={() => setChallengeFriend(null)} onSent={fetchChallenges} />
      )}
    </div>
  );
};

export default Friends;
